import { useMutation } from '@apollo/client';
import React from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';

const InvitationStatus = {
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
  SENT: 'SENT',
  ACCEPTED: 'ACCEPTED',
};

const DELETE_INVITATION_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteInvitation($id: ID!) {
    invite: deleteInvitation(id: $id) {
      id
    }
  }
`);

const RESEND_INVITATION_MUTATION = gql(/** @complexity 2 */ `
  mutation resendInvitation($id: ID!) {
    resendInvitation(id: $id) {
      id
    }
  }
`);

const APPROVE_INVITATION_MUTATION = gql(/** @complexity 2 */ `
  mutation approveInvitation($id: ID!, $approve: Boolean!) {
    invitation: approveInvitation(id: $id, approve: $approve) {
      id
      status
    }
  }
`);

const TEAMMATE_INVITATION_MENU_INVITATION_FRAGMENT = gql(`
  fragment TeammateInvitationMenu_invitation on Invitation {
    id
    name
    status
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const TEAMMATE_INVITATION_MENU_QUERY = gql(/** @complexity 4 */ `
  query TeammatesInvitationMenu {
    session: currentSession {
      organization {
        id
        invitations {
          id
        }
      }
    }
  }
`);

interface TeammateInvitationMenuProps {
  invitation: DocumentType<typeof TEAMMATE_INVITATION_MENU_INVITATION_FRAGMENT>;
}

const TeammateInvitationMenu = ({
  invitation,
}: TeammateInvitationMenuProps) => {
  const { status } = invitation;

  const dispatch = useAppDispatch();
  const [deleteInvitation] = useMutation(DELETE_INVITATION_MUTATION, {
    optimisticResponse: {
      __typename: 'RootMutationType',
      invite: { __typename: 'Invitation', id: invitation.id },
    },
    update: legacyUpdateQueryList(
      { query: TEAMMATE_INVITATION_MENU_QUERY },
      'session.organization.invitations',
      'invite',
      false,
    ),
  });
  const [resendInvitation] = useMutation(RESEND_INVITATION_MUTATION);
  const [approveInvitation] = useMutation(APPROVE_INVITATION_MUTATION);

  return (
    <OverflowMenu iconSize={16} applyNegativeMargin={false}>
      {(status === InvitationStatus.PENDING ||
        status === InvitationStatus.REJECTED) && (
        <MenuItem
          onClick={() => {
            approveInvitation({
              variables: { id: invitation.id, approve: true },
            }).then(() => {
              dispatch(
                UIActions.pushToast(
                  {
                    level: 'success',
                    message: `${invitation.name}'s invitation has been approved.`,
                  },
                  3000,
                ),
              );
            });
          }}
        >
          Approve invitation
        </MenuItem>
      )}
      {status === InvitationStatus.PENDING && (
        <MenuItem
          onClick={() => {
            approveInvitation({
              variables: { id: invitation.id, approve: false },
            }).then(() => {
              dispatch(
                UIActions.pushToast(
                  {
                    level: 'success',
                    // TODO: Fix this the next time the file is edited.

                    message: `${invitation.name}'s invitation has been rejected.`,
                  },
                  3000,
                ),
              );
            });
          }}
        >
          Reject invitation
        </MenuItem>
      )}
      {status !== InvitationStatus.PENDING &&
        status !== InvitationStatus.REJECTED && (
          <MenuItem
            onClick={() => {
              resendInvitation({ variables: { id: invitation.id } }).then(
                () => {
                  dispatch(
                    UIActions.pushToast(
                      { level: 'success', message: `Invite resent.` },
                      3000,
                    ),
                  );
                },
              );
            }}
          >
            Resend Invite
          </MenuItem>
        )}
      <MenuSeparator />
      <MenuItem
        danger
        icon={trashIcon}
        onClick={() => {
          deleteInvitation({ variables: { id: invitation.id } });
        }}
      >
        Delete
      </MenuItem>
    </OverflowMenu>
  );
};

export default TeammateInvitationMenu;
